// src/encounter/ips-social-history-dialog.tsx
// Add/Edit dialog for the IPS-rail Social History section. PRESENTATIONAL — composes DS Modal +
// CodeSearchField (observation type, host-owned search) + Select (quantity/frequency from the
// master GetSocialHabitQtyList) + DatePicker (From / To window) + TextArea (comment). No
// hooks/network: the host supplies search results + the submit/search callbacks (mirrors
// IpsConditionDialog / IpsAllergyDialog). On submit it resolves the selection into a
// SocialHistoryFormValue; the rail turns that into a wire request via buildSocialHistoryRequest +
// useSaveSocialHistory.
//
// DATE CONTROL: DynamicDateInput (partial/duration-capable) for From/To — this IS the follow-up the
// previous comment here anticipated. Flutter uses DynamicDatePicker on BOTH fields
// (`history_tab.dart:517,553`), whose helper text reads "Enter date (DD/MM/YYYY, MM/YYYY, YYYY or
// duration eg: 1Y or 2M or 3D)". The wire already carried the pair — fromDisplayTime/fromTime and
// toDisplayTime/toTime — so the display side holds the VERBATIM text and the ISO side the resolved
// instant. The old DS DatePicker took a Date and reformatted it to DD/MM/YYYY, which discarded the
// verbatim text and made "3y" or a bare "2019" unenterable.
import { useMemo, useState } from 'react';
import {
  Button,
  Select,
  TextArea,
  CodeSearchField,
  HelperText,
  type CodeSearchResult,
  type SelectOption,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import { DynamicDateInput } from '@/patients/components/chart/dynamic-date-input';
import type {
  PatientSocialHistory,
  SocialHabitQty,
  SocialHistoryCodeRow,
} from '@/types/patient-chart-api';
import type { SocialHistoryFormValue } from '@/encounter/lib/to-social-history-request';

export interface IpsSocialHistoryDialogProps {
  open: boolean;
  /** Present → Edit mode; absent → Add mode. */
  editing?: PatientSocialHistory | null;

  // Type search (host-owned).
  query: string;
  onQueryChange: (next: string) => void;
  results: CodeSearchResult[];
  searching?: boolean;
  /** Resolve a clicked result id → the full master SocialHistoryCodeRow (carries category). */
  resolveType: (result: CodeSearchResult) => SocialHistoryCodeRow | null;

  // Quantity (host-owned master list).
  quantityOptions: SocialHabitQty[];
  quantityLoading?: boolean;

  saving?: boolean;
  onSubmit: (value: SocialHistoryFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';
/** Local-date parts from a Date (no UTC shift) — mirrors conditions.ts buildResolveDateFields:
 *  ISO no-Z at local midnight + a DD/MM/YYYY display partner. */

/** Seed a partial-date field, preferring the stored VERBATIM display so "3y" round-trips as "3y"
 *  rather than being rewritten as the date it resolved to. */
function seed(display: string | null | undefined, iso: string | null | undefined) {
  return { displayValue: display ?? '', isoValue: iso ?? null };
}

/** Outer gate + keyed remount; the inner form initialises state from `editing` via lazy
 *  useState initialisers (no setState-in-effect). */
export function IpsSocialHistoryDialog(props: IpsSocialHistoryDialogProps) {
  if (!props.open) return null;
  return <IpsSocialHistoryDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsSocialHistoryDialogForm({
  editing = null,
  query,
  onQueryChange,
  results,
  searching,
  resolveType,
  quantityOptions,
  quantityLoading,
  saving,
  onSubmit,
  onClose,
}: IpsSocialHistoryDialogProps) {
  const isEditing = !!editing?.identifier;
  const [code, setCode] = useState<SocialHistoryCodeRow | null>(() =>
    editing?.code
      ? { code: editing.code.code ?? null, display: editing.code.display ?? null, system: editing.code.system ?? null, category: null }
      : null,
  );
  const [quantityCode, setQuantityCode] = useState<string>(() => editing?.historyCode?.code ?? '');
  const [fromDate, setFromDate] = useState(() => seed(editing?.fromDisplayTime, editing?.fromTime));
  const [toDate, setToDate] = useState(() => seed(editing?.toDisplayTime, editing?.toTime));
  const [comment, setComment] = useState<string>(() => editing?.comment ?? '');
  const [error, setError] = useState<string | null>(null);

  // Defensive — the master quantity list could surface a wrapped/under-typed shape; map ONCE so
  // the Select render AND the submit-handler find() lookup see the same rows.
  const quantityRows = useMemo<SocialHabitQty[]>(
    () =>
      (quantityOptions ?? []).map((r) => ({
        code: r.code ?? null,
        Definition: r.Definition ?? null,
        display: r.display ?? null,
        system: r.system ?? null,
        description: r.description ?? null,
        shortName: r.shortName ?? null,
      })),
    [quantityOptions],
  );
  const quantitySelectOptions = useMemo<SelectOption<string>[]>(
    () => quantityRows.map((r) => ({ value: r.code ?? '', label: r.display ?? r.code ?? '—' })),
    [quantityRows],
  );

  function handleSubmit() {
    if (!code) {
      setError('Pick a type.');
      return;
    }
    if (!quantityCode) {
      setError('Pick a quantity.');
      return;
    }
    // The quantity master list loads on dialog-open (host useSocialHabitQtyList). If it hasn't
    // resolved (or the record's stored quantity isn't in the current list), we BLOCK submit with a
    // visible error rather than save — so a stale/empty dropdown can NEVER silently clear the
    // quantity on an edit (Codex #work-reviews). The user re-picks once the list is present.
    const habitRow = quantityRows.find((r) => r.code === quantityCode) ?? null;
    if (!habitRow) {
      setError('Pick a quantity.');
      return;
    }
    if (!fromDate.isoValue) {
      setError(
        fromDate.displayValue.trim()
          ? 'That From date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.'
          : 'Enter a From date.',
      );
      return;
    }
    // To is optional (an ongoing habit has no end) — but text that does not parse is an error, not
    // an omission, or the user silently loses what they typed.
    if (!toDate.isoValue && toDate.displayValue.trim()) {
      setError('That To date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.');
      return;
    }
    onSubmit({
      code,
      historyCode: habitRow,
      fromTime: fromDate.isoValue,
      fromDisplay: fromDate.displayValue.trim() || null,
      toTime: toDate.isoValue,
      toDisplay: toDate.displayValue.trim() || null,
      comment: comment.trim() ? comment : null,
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit social history' : 'Add social history'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save social history',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Type */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Type</span>
          {code ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {code.display ?? code.code ?? '—'}
                {code.code ? (
                  <span className="text-text-hint"> · {code.code}</span>
                ) : null}
              </span>
              <Button variant="tertiary" size="sm" onClick={() => setCode(null)} disabled={saving}>
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={query}
              onChange={onQueryChange}
              results={results}
              loading={searching}
              placeholder="Search social history (e.g. smoking, alcohol)…"
              onResultClick={(r) => {
                const picked = resolveType(r);
                if (picked) {
                  setCode(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Quantity */}
        <div className={FIELD_GROUP}>
          <Select
            label="Quantity"
            options={quantitySelectOptions}
            value={quantityCode || undefined}
            onChange={setQuantityCode}
            disabled={saving || quantityLoading}
            placeholder={quantityLoading ? 'Loading…' : 'Pick a quantity'}
          />
        </div>

        {/* From / To window */}
        <div className={FIELD_GROUP}>
          <DynamicDateInput
            label="From"
            required
            displayValue={fromDate.displayValue}
            isoValue={fromDate.isoValue}
            onChange={setFromDate}
            disabled={saving}
          />
        </div>
        <div className={FIELD_GROUP}>
          <DynamicDateInput
            label="To"
            displayValue={toDate.displayValue}
            isoValue={toDate.isoValue}
            onChange={setToDate}
            disabled={saving}
          />
        </div>

        {/* Comment */}
        <div className={FIELD_GROUP}>
          <TextArea
            label="Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional context, e.g. quit attempts, intent to quit…"
            rows={3}
            maxLength={100}
          />
        </div>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
